import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { showToast } from '@lib/utils/toastmaster';
import { extractObjectId } from '@lib/utils/url';
import { ActionButton } from '@pages/ActionButton';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { libraryMediaPhrase, libraryRemovalFor } from '../libraryRemoval';
import css from './MobileDeleteLibraryDrawer.module.scss';

type MobileDeleteLibraryDrawerProps = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  libraryId: string;
  currentName: string;
  onDeleted: () => void;
  onPressingChange?: (pressing: boolean) => void;
};

export const MobileDeleteLibraryDrawer = observer(
  ({
    open,
    onClose,
    onOpen,
    libraryId,
    currentName,
    onDeleted,
    onPressingChange,
  }: MobileDeleteLibraryDrawerProps) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const libraryManager = useLibraryManager();
    const navigateTo = useNavigate();
    const location = useLocation();

    const library = libraryManager.libraryById(libraryId);
    const removal = libraryRemovalFor(library);

    const handleDelete = async () => {
      try {
        setIsDeleting(true);
        await libraryManager.deleteLibrary(libraryId);
        showToast(removal === 'delete' ? 'library-delete-success' : 'library-detach-success');
        onClose();
        onDeleted();
        setIsDeleting(false);

        const libraryMatch = location.pathname.match(/^\/library\/(.+?)(?:\/|$)/);
        if (libraryMatch) {
          const currentLibraryId = extractObjectId(libraryMatch[1]);
          if (currentLibraryId === libraryId) {
            navigateTo('/libraries');
          }
        }
      } catch (error) {
        showToast(removal === 'delete' ? 'library-delete-failed' : 'library-detach-failed', {
          error,
        });
        setIsDeleting(false);
        // The release never reached cancelPress: the button went disabled when the hold fired
        // and React drops mouse-up on disabled buttons. Reset the wipe so a retry can start.
        onPressingChange?.(false);
      }
    };

    // Long-press to delete: user must hold for 3s to confirm
    const HOLD_DURATION = 3000;
    const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleDeleteRef = useRef(handleDelete);
    handleDeleteRef.current = handleDelete;

    // Clean up hold timer on unmount to prevent orphaned deletes
    useEffect(() => {
      return () => {
        if (holdTimer.current) {
          clearTimeout(holdTimer.current);
          holdTimer.current = null;
        }
      };
    }, []);

    const startPress = useCallback(() => {
      if (isDeleting || holdTimer.current) return; // already pressing or deleting
      onPressingChange?.(true);
      holdTimer.current = setTimeout(() => {
        // The hold is complete, so the ref no longer guards a running timer. Don't reset the
        // pressing state here: keep the wipe visible until the card unmounts on success.
        holdTimer.current = null;
        handleDeleteRef.current();
      }, HOLD_DURATION);
    }, [isDeleting, onPressingChange]);

    const cancelPress = useCallback(() => {
      onPressingChange?.(false);
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
    }, [onPressingChange]);

    return (
      <MobileDrawer open={open} onClose={onClose} onOpen={onOpen} frosted noBackdropBlur>
        <div className={css.content}>
          <span className={css.title}>
            {removal === 'delete' ? 'Delete this library?' : 'Remove this library?'}
          </span>
          <span className={css.description}>
            {removal === 'delete' ? (
              <>
                {libraryMediaPhrase(library?.stats?.count)} in <b>"{currentName}"</b> will be
                permanently deleted. This cannot be undone.
              </>
            ) : (
              <>
                <b>"{currentName}"</b> will be removed from your libraries. Its media isn't deleted:{' '}
                {library?.otherOwnerName
                  ? `${library.otherOwnerName} still owns it.`
                  : 'its owner keeps it.'}
              </>
            )}
          </span>
          <div className={css.actions}>
            <ActionButton
              variant="secondary"
              onClick={() => {
                trackingStore.libraryDeleteCancelled({ libraryId });
                onClose();
              }}
              fullWidth
            >
              Cancel
            </ActionButton>
            {removal === 'delete' ? (
              <ActionButton
                variant="danger"
                disabled={isDeleting}
                fullWidth
                onMouseDown={startPress}
                onMouseUp={cancelPress}
                onMouseLeave={cancelPress}
                onTouchStart={startPress}
                onTouchEnd={cancelPress}
                onTouchCancel={cancelPress}
              >
                {isDeleting ? 'Deleting library...' : 'Hold to delete'}
              </ActionButton>
            ) : (
              <ActionButton variant="danger" disabled={isDeleting} fullWidth onClick={handleDelete}>
                {isDeleting ? 'Removing library...' : 'Remove library'}
              </ActionButton>
            )}
          </div>
        </div>
      </MobileDrawer>
    );
  },
);
